import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CommonSidebar from '../Components/CommonSidebar';
import CommonHeader from '../Components/CommonHeader';
import CommonButton from '../Components/CommonButton';
import ModuleIcon from '../Components/ModuleIcon';
import { getUserPlanInfoService } from '../services';
import '../styles/TpcrmPortal.scss';

// Icon names from the shared ModuleIcon catalogue (src/Components/ModuleIcon.js)
// — same component the AI Adoption Portal's sidebar uses, so nav glyphs are
// consistent across portals instead of one-off inline SVGs per portal.
const navItems = [
    // Dashboard leads: it is the portal's landing page and the only read-only
    // overview, so it belongs above the working screens. /tpcrm/analytics was
    // the old path and now redirects here.
    { to: '/tpcrm/dashboard', label: 'Dashboard', icon: 'dashboard' },
    { to: '/tpcrm/vendors', label: 'Vendors', icon: 'building' },
    { to: '/tpcrm/questionnaires', label: 'Questionnaires', icon: 'questions' },
    { to: '/tpcrm/assessments', label: 'Assessments', icon: 'audit' },
];

// Shared navy sidebar + light topbar — same CommonSidebar/CommonHeader pair
// the AI Adoption Portal uses (src/AIAdoptionPortal/AIAdoptionPortal.js), so
// every portal shares one visual shell instead of hand-rolled chrome.
function TpcrmPortalContainer({ children }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [planInfo, setPlanInfo] = useState(null);

    useEffect(() => {
        let cancelled = false;
        getUserPlanInfoService()
            .then(response => {
                if (cancelled) return;
                const info = response?.statusCode?.response_data?.data?.plan_info;
                if (info) setPlanInfo(info);
            })
            .catch(() => { /* header just omits the plan badge on failure */ });
        return () => { cancelled = true; };
    }, []);

    // Pages reachable only via a deep link (e.g. the assessment-review page,
    // opened from a specific vendor's "View Response") don't match any
    // sidebar nav item's `to` prefix — give them their own title instead of
    // falling back to whichever nav item happens to be first.
    const activeItem = navItems.find(item => location.pathname.startsWith(item.to));
    const pageTitle = activeItem?.label
        || (location.pathname.startsWith('/vendor/assessment/') ? 'Assessment Review' : 'TPCRM Portal');

    // "Back" is contextual. On one of the portal's own nav pages, back means
    // leaving the portal for the dashboard. On a deep page — the assessment
    // review page, which no sidebar item points at — dropping the user all the
    // way out to the dashboard overshoots: they came from inside the portal, so
    // back should land them in the list that page belongs to.
    //
    // `activeItem` already separates the two: a deep page matches no nav item's
    // `to` prefix, which is the same test the page title above uses.
    // `?module=third-party` lands on the Third Party widget the portal is
    // launched from, instead of the top of the dashboard. Tabs.js maps that key
    // to its scroll section (MODULE_SECTIONS) — the same mechanism the Risk
    // Registry deep link already uses.
    const backTarget = activeItem
        ? { label: '← Back to Dashboard', to: '/product-dashboard' }
        : { label: '← Back to Assessments', to: '/tpcrm/assessments' };

    const sidebarGroups = [{
        items: navItems.map(item => ({ ...item, svgIcon: <ModuleIcon name={item.icon} size={18} /> })),
    }];

    return (
        <div className="tpcrm-shell">
            <CommonSidebar
                mode="link"
                subtitle="TPCRM Portal"
                groups={sidebarGroups}
                ariaLabel="TPCRM navigation"
                style={{ gridArea: 'sidebar', height: '100%', minHeight: 0, overflowY: 'auto' }}
            />

            <CommonHeader
                className="tpcrm-header"
                planInfo={planInfo}
                currentScreen={<span className="tpcrm-header-title">{pageTitle}</span>}
                backToDashboard={<CommonButton text={backTarget.label} onClick={() => navigate(backTarget.to)} />}
            />

            <main className="tpcrm-main">
                <div className="tpcrm-portal-content">
                    {children}
                </div>
            </main>
        </div>
    );
}

export default TpcrmPortalContainer;
